function parsePositiveIntEnv(raw: string | undefined, fallback: number): number {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
}

/** Mirrors `FilesInterceptor('images', 20, …)` on the assets service. */
export const ASSET_CREATE_MAX_IMAGE_FILES = 20;

/** Owner create form: one main image (list thumbnail) plus up to nine additional. */
export const ASSET_CREATE_UI_MAX_IMAGES = 10;

export const ASSET_CREATE_MAX_IMAGE_FILE_BYTES = 15 * 1024 * 1024;

/**
 * Client-side target after resize/JPEG re-encode before multipart upload (reduces proxy timeouts).
 * Override with **`REACT_APP_ASSET_IMAGE_TARGET_MAX_BYTES`** (bytes). Server still enforces
 * **`ASSET_CREATE_MAX_IMAGE_FILE_BYTES`**.
 */
/**
 * Per-file target after client resize. Default **120 KB** — this API/nginx often rejects multipart totals
 * above ~500 KB and JSON PATCH above ~100 KB (observed against the default dev host).
 * Override with **`REACT_APP_ASSET_IMAGE_TARGET_MAX_BYTES`**.
 */
export const ASSET_UPLOAD_TARGET_MAX_BYTES = parsePositiveIntEnv(
  typeof process !== 'undefined' ? process.env.REACT_APP_ASSET_IMAGE_TARGET_MAX_BYTES : undefined,
  120 * 1024,
);

/** Longest canvas edge (px) before upload. **`REACT_APP_ASSET_IMAGE_MAX_EDGE`**. */
export const ASSET_UPLOAD_MAX_EDGE = parsePositiveIntEnv(
  typeof process !== 'undefined' ? process.env.REACT_APP_ASSET_IMAGE_MAX_EDGE : undefined,
  2048,
);

/**
 * When **`false`** (`REACT_APP_ASSET_IMAGE_COMPRESS=false`), picked files are sent as-is (subject to server size limit).
 */
export const ASSET_IMAGE_COMPRESS_BEFORE_UPLOAD =
  typeof process !== 'undefined' && process.env.REACT_APP_ASSET_IMAGE_COMPRESS === 'false' ? false : true;

/**
 * Multipart text field carrying stringified JSON for CreateAssetDto (same as JSON-only create).
 * Default `payload` matches the assets service multipart contract; override with
 * `REACT_APP_ASSET_CREATE_FORM_JSON_FIELD` if your server uses another name (e.g. `body`).
 */
export const ASSET_CREATE_FORM_JSON_FIELD =
  typeof process !== 'undefined' && process.env.REACT_APP_ASSET_CREATE_FORM_JSON_FIELD
    ? process.env.REACT_APP_ASSET_CREATE_FORM_JSON_FIELD
    : 'payload';

/**
 * When **`true`**, multipart **PATCH** `/assets/:id` puts **only** the sanitized `images` URL list in the
 * JSON part (file parts unchanged), not the full asset. Use when **POST /assets/create** multipart works but
 * **PATCH** multipart fails with timeouts / `Failed to fetch`: the full PATCH JSON is often much larger
 * (existing GCS URLs, opening times, facilities, …). Safe **only** if your API **merges** partial updates;
 * if PATCH replaces the whole entity, leave this **`false`** (default).
 */
/** In dev, default **minimal** JSON on multipart PATCH unless explicitly set to **`false`**. */
export const ASSET_PATCH_MULTIPART_JSON_MINIMAL =
  typeof process !== 'undefined' &&
  (process.env.REACT_APP_ASSET_PATCH_MULTIPART_JSON_MINIMAL === 'true' ||
    (process.env.NODE_ENV === 'development' &&
      process.env.REACT_APP_ASSET_PATCH_MULTIPART_JSON_MINIMAL !== 'false'));
